import React, { useEffect, useState } from 'react';
import { X, CheckCircle, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { courseAPI, registrationAPI, getErrorMessage } from '../services/api';

const statusStyles = {
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

export default function RegistrationDetail({ registrationId, onClose, onUpdated }) {
  const [registration, setRegistration] = useState(null);
  const [courses, setCourses] = useState([]);
  const [courseId, setCourseId] = useState('');
  const [feeOverride, setFeeOverride] = useState('');
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [regRes, coursesRes] = await Promise.all([
          registrationAPI.getRegistration(registrationId),
          courseAPI.listCourses({ activeOnly: true }),
        ]);
        setRegistration(regRes.data);
        const centerCourses = (coursesRes.data.items || []).filter(
          (c) => !regRes.data.center_id || !c.center_id || c.center_id === regRes.data.center_id
        );
        setCourses(centerCourses);
      } catch (err) {
        toast.error(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [registrationId]);

  const selectedCourse = courses.find((c) => c.id === courseId);

  const handleApprove = async () => {
    if (!courseId) {
      toast.error('Select a course to assign');
      return;
    }
    setUpdating(true);
    try {
      await registrationAPI.approveRegistration(registrationId, {
        courseId,
        assignedFee: feeOverride ? Number(feeOverride) : null,
      });
      toast.success('Approved — course fee sent to parent');
      onUpdated?.();
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setUpdating(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Enter a rejection reason');
      return;
    }
    setUpdating(true);
    try {
      await registrationAPI.rejectRegistration(registrationId, rejectReason.trim());
      toast.success('Registration rejected');
      onUpdated?.();
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setUpdating(false);
    }
  };

  if (loading || !registration) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8"><div className="animate-spin h-8 w-8 border-b-2 border-blue-500 rounded-full" /></div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
          <h2 className="text-xl font-bold">Registration Request</h2>
          <button onClick={onClose}><X className="w-6 h-6 text-gray-500" /></button>
        </div>

        <div className="p-6 space-y-4 text-sm">
          <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${statusStyles[registration.registration_status]}`}>
            {registration.registration_status}
          </span>

          <div className="grid grid-cols-2 gap-3">
            <div><p className="text-gray-500">Parent</p><p className="font-medium">{registration.full_name}</p></div>
            <div><p className="text-gray-500">Phone</p><p className="font-medium">{registration.phone_number || 'N/A'}</p></div>
            <div><p className="text-gray-500">Child</p><p className="font-medium">{registration.child_name}</p></div>
            <div><p className="text-gray-500">Grade</p><p className="font-medium">{registration.child_grade || 'N/A'}</p></div>
            <div><p className="text-gray-500">Center</p><p className="font-medium">{registration.center_name || 'N/A'}</p></div>
          </div>

          {registration.registration_status === 'approved' && (
            <div className="p-3 bg-green-50 rounded-lg">
              <p><b>Course:</b> {registration.course_name}</p>
              <p><b>Fee:</b> {registration.assigned_fee} Birr</p>
            </div>
          )}

          {registration.registration_status === 'pending' && (
            <div className="border-t pt-4 space-y-3">
              <label className="block font-medium">Assign course *</label>
              <select className="w-full border rounded-lg px-3 py-2" value={courseId} onChange={(e) => { setCourseId(e.target.value); setFeeOverride(''); }}>
                <option value="">Select course...</option>
                {courses.map((c) => (
                  <option key={c.id} value={c.id}>{c.name} — {Number(c.fee).toFixed(2)} Birr</option>
                ))}
              </select>
              {selectedCourse && (
                <input
                  className="w-full border rounded-lg px-3 py-2"
                  type="number"
                  step="0.01"
                  placeholder={`Fee override (default ${selectedCourse.fee} Birr)`}
                  value={feeOverride}
                  onChange={(e) => setFeeOverride(e.target.value)}
                />
              )}
              {!showRejectForm ? (
                <div className="flex gap-2">
                  <button onClick={handleApprove} disabled={updating} className="flex-1 flex items-center justify-center gap-2 bg-green-600 text-white py-2 rounded-lg">
                    <CheckCircle className="w-4 h-4" /> Approve & Assign
                  </button>
                  <button onClick={() => setShowRejectForm(true)} className="flex-1 flex items-center justify-center gap-2 bg-red-600 text-white py-2 rounded-lg">
                    <XCircle className="w-4 h-4" /> Reject
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <textarea className="w-full border rounded-lg px-3 py-2" rows={3} placeholder="Rejection reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
                  <button onClick={handleReject} disabled={updating} className="w-full bg-red-600 text-white py-2 rounded-lg">Confirm Reject</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
